// @ts-nocheck
import { useState } from "react"
import { Link } from "react-router-dom"
import { supabaseClient } from "@/lib/supabase/client"
import { Avatar } from "@/components/ui/Avatar"
import { cn } from "@/lib/utils/cn"

export function PostCard({ post, userId }: { post: any; userId: string | null }) {
  const [vote, setVote] = useState<"up" | "down" | null>(post.user_vote)
  const [score, setScore] = useState(post.vote_score || 0)
  const [voting, setVoting] = useState(false)
  const [bookmarked, setBookmarked] = useState(post.user_bookmarked || false)

  const handleVote = async (type: "up" | "down") => {
    if (!userId || voting) return
    setVoting(true)
    const prev = vote
    let delta = 0
    let newVote: "up" | "down" | null

    if (prev === type) { newVote = null; delta = type === "up" ? -1 : 1 }
    else if (!prev) { newVote = type; delta = type === "up" ? 1 : -1 }
    else { newVote = type; delta = type === "up" ? 2 : -2 }

    setVote(newVote)
    setScore(s => s + delta)

    try {
      if (newVote === null) {
        await supabaseClient.from("votes").delete().eq("post_id", post.id).eq("user_id", userId)
      } else {
        await supabaseClient.from("votes").upsert({ post_id: post.id, user_id: userId, vote_type: newVote } as any)
      }
    } catch { setVote(prev); setScore(s => s - delta) }
    finally { setVoting(false) }
  }

  const author = post.author || post.users
  const topic = post.topic || post.post_topics?.[0]?.topics
  const postUrl = `/post/${post.id}/${post.slug || "post"}`
  const timeAgo = formatAgo(post.published_at || post.created_at)

  return (
    <article className="post-card flex group">
      {/* Vote column */}
      <div className="vote-col flex-shrink-0">
        <button
          onClick={() => handleVote("up")}
          className={cn("vote-btn", vote === "up" && "active-up")}
          aria-label="Upvote"
        >
          <svg className="w-4 h-4" fill={vote === "up" ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
        <span className={cn("vote-score", vote === "up" && "up", vote === "down" && "down")}>
          {fmtNum(score)}
        </span>
        <button
          onClick={() => handleVote("down")}
          className={cn("vote-btn", vote === "down" && "active-down")}
          aria-label="Downvote"
        >
          <svg className="w-4 h-4" fill={vote === "down" ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 p-2.5">
        {/* Meta */}
        <div className="flex items-center gap-1.5 flex-wrap text-xs text-muted-fg mb-1.5">
          {topic && (
            <Link to={`/topics/${topic.slug}`} className="topic-pill font-bold hover:text-brand">
              t/{topic.name}
            </Link>
          )}
          {topic && <span>·</span>}
          <span>Posted by</span>
          {author && (
            <Link to={`/profile/${author.username}`} className="hover:underline hover:text-foreground transition-colors">
              u/{author.username}
            </Link>
          )}
          <span>·</span>
          <span>{timeAgo}</span>
        </div>

        {/* Title */}
        <Link to={postUrl} className="block">
          <h2 className="text-sm font-semibold leading-snug text-foreground hover:text-brand transition-colors line-clamp-3 mb-1.5">
            {post.title}
          </h2>
        </Link>

        {/* Body preview */}
        {post.content && (
          <p className="text-xs text-muted-fg line-clamp-2 mb-2 leading-relaxed">
            {stripMd(post.content)}
          </p>
        )}

        {/* Actions */}
        <div className="post-actions">
          <Link to={postUrl} className="action-btn">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {fmtNum(post.comment_count || 0)} Comments
          </Link>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(window.location.origin + postUrl)
            }}
            className="action-btn"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Share
          </button>
          {userId && (
            <button
              onClick={async () => {
                setBookmarked(b => !b)
                try {
                  if (bookmarked) {
                    await supabaseClient.from("bookmarks").delete().eq("post_id", post.id).eq("user_id", userId)
                  } else {
                    await supabaseClient.from("bookmarks").insert({ post_id: post.id, user_id: userId } as any)
                  }
                } catch { setBookmarked(b => !b) }
              }}
              className={cn("action-btn", bookmarked && "text-brand")}
            >
              <svg className="w-3.5 h-3.5" fill={bookmarked ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              Save
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

function fmtNum(n: number) {
  if (n >= 1000) return (n / 1000).toFixed(1) + "k"
  return String(n)
}

function formatAgo(ts: string) {
  if (!ts) return ""
  const diff = (Date.now() - new Date(ts).getTime()) / 1000
  if (diff < 60) return "just now"
  if (diff < 3600) return Math.floor(diff / 60) + "m ago"
  if (diff < 86400) return Math.floor(diff / 3600) + "h ago"
  if (diff < 604800) return Math.floor(diff / 86400) + "d ago"
  return Math.floor(diff / 604800) + "w ago"
}

function stripMd(s: string) {
  return s.split("").filter(ch => !"#*_`>~[]".includes(ch)).join("").replace(/\s+/g, " ").trim()
}
